import React, { useState, useEffect, useCallback } from 'react';
import { 
  CreditCard, Plus, Trash2, Star, Loader2, AlertCircle, ShieldCheck, Building2, Banknote
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { clientPortalService } from '../../services/clientPortal.service';
import { toast } from 'sonner';

// Initialize Stripe with environment variable (fallback for backward compatibility)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card brand styles
const cardBrandStyles = {
  Visa: { color: 'text-blue-600', bg: 'bg-blue-50' },
  Mastercard: { color: 'text-orange-600', bg: 'bg-orange-50' },
  Amex: { color: 'text-blue-700', bg: 'bg-blue-50' },
  Discover: { color: 'text-orange-500', bg: 'bg-orange-50' },
  default: { color: 'text-gray-600', bg: 'bg-gray-50' }
};

// Add Card Form Component
const AddCardForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    const createSetupIntent = async () => {
      try {
        const { client_secret } = await clientPortalService.createSetupIntent();
        setClientSecret(client_secret);
      } catch (err) {
        setError('Failed to initialize payment form. Please try again.');
        console.error(err);
      }
    };
    createSetupIntent();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setLoading(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (stripeError) {
        setError(stripeError.message);
      } else if (setupIntent.status === 'succeeded') {
        toast.success('Card added successfully!');
        onSuccess();
      }
    } catch (err) {
      setError('Failed to save card');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border rounded-lg bg-gray-50">
        <CardElement
          options={{
            style: {
              base: { fontSize: '16px', color: '#1f2937', '::placeholder': { color: '#9ca3af' } },
              invalid: { color: '#ef4444' },
            },
          }}
        />
      </div>
      
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-gray-500">
        <ShieldCheck className="h-4 w-4" />
        <span>Your card information is securely processed by Stripe</span>
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600" disabled={!stripe || !clientSecret || loading}>
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Plus className="h-4 w-4 mr-2" />Add Card</>}
        </Button>
      </div>
    </form>
  );
};

// Add Bank Account Form Component - Financial Connections (Instant Verification)
const AddBankForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    const createSetupIntent = async () => {
      try {
        const data = await clientPortalService.createBankSetupIntent();
        setClientSecret(data.client_secret);
      } catch (err) {
        const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
        setError(`Failed to initialize bank connection: ${errorMsg}`);
        console.error('Bank setup error:', err.response?.data || err);
      }
    };
    createSetupIntent();
  }, []);

  const handleConnectBank = async () => {
    if (!stripe || !clientSecret) return;

    setLoading(true);
    setError(null);

    try {
      // Use Stripe's collectBankAccountForSetup for Financial Connections
      const { setupIntent, error: stripeError } = await stripe.collectBankAccountForSetup({
        clientSecret: clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: 'Account Holder',
            },
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        return;
      }

      // Check various success states
      if (setupIntent.status === 'succeeded') {
        toast.success('Bank account connected successfully!');
        onSuccess();
        return;
      }

      if (setupIntent.status === 'requires_confirmation') {
        const { error: confirmError, setupIntent: confirmedIntent } = await stripe.confirmUsBankAccountSetup(clientSecret);
        if (confirmError) {
          setError(confirmError.message);
          return;
        }
        if (confirmedIntent?.status === 'succeeded') {
          toast.success('Bank account connected successfully!');
          onSuccess();
          return;
        }
      }

      if (setupIntent.next_action?.type === 'verify_with_microdeposits') {
        toast.success('Bank account added! Micro-deposits will be sent for verification.');
        onSuccess();
        return;
      }
      
      if (setupIntent) {
        toast.success('Bank account connected!');
        onSuccess();
      }
    } catch (err) {
      setError('Failed to connect bank account. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-4 border rounded-lg bg-blue-50">
        <div className="flex items-start gap-3">
          <Building2 className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">Connect Your Bank Account</p>
            <p className="text-sm text-blue-600 mt-1">
              Securely link your bank account using Stripe's Financial Connections. 
              This allows instant verification without sharing your login credentials.
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 border rounded-lg bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-2">ACH Direct Debit Benefits:</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Lower transaction fees than credit cards</li>
          <li>• Ideal for large invoice payments</li>
          <li>• Funds transfer in 3-5 business days</li>
        </ul>
      </div>
      
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={handleConnectBank} 
          className="flex-1 bg-amber-500 hover:bg-amber-600" 
          disabled={!stripe || !clientSecret || loading}
        >
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</> : <><Building2 className="h-4 w-4 mr-2" />Connect Bank</>}
        </Button>
      </div>
    </div>
  );
};

const PaymentMethods = () => {
  const [cards, setCards] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasDefault, setHasDefault] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addType, setAddType] = useState('card'); // 'card' or 'bank'
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const [processing, setProcessing] = useState(null);

  const loadPaymentMethods = useCallback(async () => {
    try {
      const data = await clientPortalService.getAllPaymentMethods();
      setCards(data.cards || []);
      setBankAccounts(data.bank_accounts || []);
      setHasDefault(data.has_default || false);
      setStripeConfigured(data.stripe_configured !== false);
    } catch (error) {
      console.error('Error loading payment methods:', error);
      // Try legacy endpoint as fallback
      try {
        const legacyData = await clientPortalService.getPaymentMethods();
        setCards(legacyData.payment_methods || []);
        setBankAccounts([]);
        setHasDefault(legacyData.has_default || false);
        setStripeConfigured(legacyData.stripe_configured !== false);
      } catch {
        toast.error('Failed to load payment methods');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  const handleSetDefault = async (paymentMethodId) => {
    setProcessing(paymentMethodId);
    try {
      await clientPortalService.setDefaultPaymentMethod(paymentMethodId);
      toast.success('Default payment method updated');
      loadPaymentMethods();
    } catch {
      toast.error('Failed to update default');
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async () => {
    if (!methodToDelete) return;
    setProcessing(methodToDelete.id);
    try {
      await clientPortalService.deletePaymentMethod(methodToDelete.id);
      toast.success('Payment method removed');
      setDeleteConfirmOpen(false);
      setMethodToDelete(null);
      loadPaymentMethods();
    } catch {
      toast.error('Failed to remove payment method');
    } finally {
      setProcessing(null);
    }
  };

  const handleAddSuccess = () => {
    setAddDialogOpen(false);
    loadPaymentMethods();
  };

  const openAddDialog = (type) => {
    setAddType(type);
    setAddDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  const totalMethods = cards.length + bankAccounts.length;

  return (
    <div className="space-y-6" data-testid="payment-methods-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Methods</h1>
          <p className="text-gray-500">Manage your cards and bank accounts</p>
        </div>
        {stripeConfigured && (
          <div className="flex gap-2">
            <Button onClick={() => openAddDialog('card')} className="bg-amber-500 hover:bg-amber-600" data-testid="add-card-btn">
              <CreditCard className="h-4 w-4 mr-2" />Add Card
            </Button>
            <Button onClick={() => openAddDialog('bank')} variant="outline" data-testid="add-bank-btn">
              <Building2 className="h-4 w-4 mr-2" />Add Bank Account
            </Button>
          </div>
        )}
      </div>

      {/* Stripe Not Configured Warning */}
      {!stripeConfigured && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Payment Methods Not Available</p>
                <p className="text-sm text-yellow-600 mt-1">
                  Payment methods require Stripe to be configured. Contact your administrator.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Cards */}
      {stripeConfigured && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <CreditCard className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Credit/Debit Cards</p>
                  <p className="text-sm text-blue-600 mt-1">Instant payments, processed immediately</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">Bank Accounts (ACH)</p>
                  <p className="text-sm text-green-600 mt-1">Lower fees, 3-5 business day processing</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment Methods Tabs */}
      {stripeConfigured && (
        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all">All ({totalMethods})</TabsTrigger>
            <TabsTrigger value="cards">Cards ({cards.length})</TabsTrigger>
            <TabsTrigger value="banks">Bank Accounts ({bankAccounts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4 space-y-3">
            {totalMethods === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Banknote className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No payment methods saved</h3>
                  <p className="text-gray-500 mb-4">Add a card or bank account to enable quick payments</p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={() => openAddDialog('card')} className="bg-amber-500 hover:bg-amber-600">
                      <CreditCard className="h-4 w-4 mr-2" />Add Card
                    </Button>
                    <Button onClick={() => openAddDialog('bank')} variant="outline">
                      <Building2 className="h-4 w-4 mr-2" />Add Bank
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {cards.map(card => (
                  <PaymentMethodCard key={card.id} method={card} type="card" processing={processing} 
                    onSetDefault={handleSetDefault} onDelete={(m) => { setMethodToDelete(m); setDeleteConfirmOpen(true); }} />
                ))}
                {bankAccounts.map(bank => (
                  <PaymentMethodCard key={bank.id} method={bank} type="bank" processing={processing}
                    onSetDefault={handleSetDefault} onDelete={(m) => { setMethodToDelete(m); setDeleteConfirmOpen(true); }} />
                ))}
              </>
            )}
          </TabsContent>

          <TabsContent value="cards" className="mt-4 space-y-3">
            {cards.length === 0 ? (
              <Card><CardContent className="p-6 text-center text-gray-500">No cards saved</CardContent></Card>
            ) : (
              cards.map(card => (
                <PaymentMethodCard key={card.id} method={card} type="card" processing={processing}
                  onSetDefault={handleSetDefault} onDelete={(m) => { setMethodToDelete(m); setDeleteConfirmOpen(true); }} />
              ))
            )}
          </TabsContent>

          <TabsContent value="banks" className="mt-4 space-y-3">
            {bankAccounts.length === 0 ? (
              <Card><CardContent className="p-6 text-center text-gray-500">No bank accounts linked</CardContent></Card>
            ) : (
              bankAccounts.map(bank => (
                <PaymentMethodCard key={bank.id} method={bank} type="bank" processing={processing}
                  onSetDefault={handleSetDefault} onDelete={(m) => { setMethodToDelete(m); setDeleteConfirmOpen(true); }} />
              ))
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Add Payment Method Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {addType === 'card' ? <CreditCard className="h-5 w-5 text-amber-500" /> : <Building2 className="h-5 w-5 text-amber-500" />}
              {addType === 'card' ? 'Add Card' : 'Add Bank Account'}
            </DialogTitle>
            <DialogDescription>
              {addType === 'card' ? 'Add a credit or debit card' : 'Connect your bank account via ACH'}
            </DialogDescription>
          </DialogHeader>
          <Elements stripe={stripePromise}>
            {addType === 'card' ? (
              <AddCardForm onSuccess={handleAddSuccess} onCancel={() => setAddDialogOpen(false)} />
            ) : (
              <AddBankForm onSuccess={handleAddSuccess} onCancel={() => setAddDialogOpen(false)} />
            )}
          </Elements>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Payment Method?</AlertDialogTitle>
            <AlertDialogDescription>
              {methodToDelete?.type === 'card' 
                ? `Remove ${methodToDelete?.brand} •••• ${methodToDelete?.last4}?`
                : `Remove ${methodToDelete?.bank_name} •••• ${methodToDelete?.last4}?`}
              {methodToDelete?.is_default && (
                <span className="block mt-2 text-amber-600 font-medium">
                  This is your default payment method.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              {processing === methodToDelete?.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Reusable Payment Method Card Component
const PaymentMethodCard = ({ method, type, processing, onSetDefault, onDelete }) => {
  const isCard = type === 'card';
  const brandStyle = isCard ? (cardBrandStyles[method.brand] || cardBrandStyles.default) : { color: 'text-green-600', bg: 'bg-green-50' };

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`payment-method-${method.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${brandStyle.bg}`}>
              {isCard ? <CreditCard className={`h-6 w-6 ${brandStyle.color}`} /> : <Building2 className={`h-6 w-6 ${brandStyle.color}`} />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-900">
                  {isCard ? `${method.brand} •••• ${method.last4}` : `${method.bank_name} •••• ${method.last4}`}
                </p>
                {method.is_default && (
                  <Badge className="bg-amber-100 text-amber-700"><Star className="h-3 w-3 mr-1" />Default</Badge>
                )}
                {!isCard && (
                  <Badge variant="outline" className="text-green-600 border-green-300">ACH</Badge>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {isCard ? `Expires ${String(method.exp_month).padStart(2, '0')}/${method.exp_year}` : `${method.account_type} account`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!method.is_default && (
              <Button variant="outline" size="sm" onClick={() => onSetDefault(method.id)} disabled={processing === method.id}>
                {processing === method.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Star className="h-4 w-4 mr-1" />Set Default</>}
              </Button>
            )}
            <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={() => onDelete(method)} disabled={processing === method.id}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentMethods;
